import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { getCollectionPageData, getItemListData } from "@/lib/structuredData";
import { useExploreArtworks } from "@/hooks/useExploreArtworks";
import { ExploreFilters } from "@/components/explore/ExploreFilters";
import { ExploreActiveFilters } from "@/components/explore/ExploreActiveFilters";
import { ExploreResults } from "@/components/explore/ExploreResults";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Explore() {
  const {
    searchText,
    setSearchText,
    category,
    setCategory,
    sortBy,
    setSortBy,
    artworkType,
    setArtworkType,
    selectedArtist,
    setSelectedArtist,
    advancedFilters,
    setAdvancedFilters,
    artists,
    availableCategories,
    filteredArtworks,
    totalCount,
    isLoading,
    isFetchingNextPage,
    observerTarget,
  } = useExploreArtworks();

  const pageTitle = "Utforska konstverk";
  const pageDescription = "Upptäck och köp konst från svenska konstnärer - original och prints";

  const getPrimaryImage = (artwork: any): string => {
    const images = artwork.artwork_images || [];
    if (images.length === 0) return "/placeholder.svg";
    const sorted = [...images].sort((a: any, b: any) => {
      if (a.is_room_mockup && !b.is_room_mockup) return -1;
      if (!a.is_room_mockup && b.is_room_mockup) return 1;
      return (a.display_order || 0) - (b.display_order || 0);
    });
    return sorted[0].image_url || "/placeholder.svg";
  };

  const structuredData = filteredArtworks && filteredArtworks.length > 0
    ? getItemListData(
        filteredArtworks.map((a: any) => ({
          id: a.id,
          title: a.title,
          description: a.description,
          price: a.price,
          image_url: getPrimaryImage(a),
          artist_name: a.public_profiles?.full_name || a.public_profiles?.username
        })),
        pageTitle
      )
    : getCollectionPageData(pageTitle, pageDescription);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Utforska konstverk"
        description="Upptäck och köp konst från svenska konstnärer. Bläddra bland original och prints."
        canonicalUrl="/explore"
        keywords={["köp konst", "konstverk", "original", "prints", "svenska konstnärer"]}
        structuredData={structuredData}
      />
      <Navbar />
      
      <div className="container py-2 sm:py-4 md:py-8 px-4 pb-24 sm:pb-32 md:pb-40">
        <div className="mb-3 sm:mb-4 md:mb-8">
          <h1 className="mb-1 text-xl sm:text-2xl md:text-4xl font-bold">
            Utforska konstverk
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground hidden sm:block">
            Hitta det perfekta konstverket från våra talangfulla konstnärer
          </p>
        </div>

        {/* Search input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Sök efter konstverk..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-10 pr-10 h-10"
          />
          {searchText && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
              onClick={() => setSearchText("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <ExploreFilters
          category={category}
          setCategory={setCategory}
          selectedArtist={selectedArtist}
          setSelectedArtist={setSelectedArtist}
          artworkType={artworkType}
          setArtworkType={setArtworkType}
          sortBy={sortBy}
          setSortBy={setSortBy}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          artists={artists}
          availableCategories={availableCategories}
        />

        <ExploreActiveFilters
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
        />

        <ExploreResults
          isLoading={isLoading}
          artworks={filteredArtworks}
          totalCount={totalCount}
          isFetchingNextPage={isFetchingNextPage}
          observerTarget={observerTarget}
        />
      </div>
      
      <Footer />
    </div>
  );
}
